import React, { useMemo, useRef, useState } from 'react'
import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '../../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../../config/api'

const STATUS_LABEL = {
  matched: { label: 'Matched', cls: 'bg-green-100 text-green-800' },
  name_mismatch: { label: 'Name mismatch', cls: 'bg-amber-100 text-amber-800' },
  not_found: { label: 'Not found', cls: 'bg-red-100 text-red-800' },
  invalid: { label: 'Invalid', cls: 'bg-gray-100 text-gray-500' },
  already_open: { label: 'Already open', cls: 'bg-orange-100 text-orange-800' },
  duplicate_ref: { label: 'Already imported', cls: 'bg-gray-100 text-gray-700' },
}

function StatusBadge({ status }) {
  const meta = STATUS_LABEL[status] || { label: status, cls: 'bg-gray-100 text-gray-700' }
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${meta.cls}`}>{meta.label}</span>
}

/**
 * ECDD Excel bulk import — two templates:
 *  - open_new: open blank ECDDs for members
 *  - with_data: import members + existing ECDD answers/status
 */
export default function EcddBulkImportModal({ open, onClose, venues = [], onCommitted }) {
  const fileRef = useRef(null)
  const [mode, setMode] = useState('open_new')
  const [defaultVenueId, setDefaultVenueId] = useState('')
  const [fileName, setFileName] = useState('')
  const [loading, setLoading] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [error, setError] = useState(null)
  const [summary, setSummary] = useState(null)
  const [rows, setRows] = useState([])
  const [includeNameMismatches, setIncludeNameMismatches] = useState(false)
  const [includeAlreadyOpen, setIncludeAlreadyOpen] = useState(false)
  const [selected, setSelected] = useState(() => new Set())
  const [commitResult, setCommitResult] = useState(null)

  const reset = () => {
    setFileName('')
    setLoading(false)
    setCommitting(false)
    setError(null)
    setSummary(null)
    setRows([])
    setIncludeNameMismatches(false)
    setIncludeAlreadyOpen(false)
    setSelected(new Set())
    setCommitResult(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const handleClose = () => {
    reset()
    onClose?.()
  }

  const defaultSelectable = (row) => {
    if (row.status_code === 'matched') return true
    if (row.status_code === 'name_mismatch' && includeNameMismatches) return true
    if (row.status_code === 'already_open' && includeAlreadyOpen) return true
    return false
  }

  const selectableRows = useMemo(
    () => rows.filter((r) => defaultSelectable(r)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, includeNameMismatches, includeAlreadyOpen]
  )

  const downloadTemplate = async () => {
    try {
      setError(null)
      const res = await authFetch(
        getLocalApiUrl(`/api/ecdd/bulk-import/template?mode=${encodeURIComponent(mode)}`)
      )
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j.error || `HTTP ${res.status}`)
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = mode === 'with_data' ? 'ecdd-import-with-data.xlsx' : 'ecdd-import-open-new.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e?.message || 'Failed to download template')
    }
  }

  const runPreview = async (file) => {
    if (!file) return
    setLoading(true)
    setError(null)
    setCommitResult(null)
    setFileName(file.name)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('mode', mode)
      if (defaultVenueId) fd.append('default_venue_id', defaultVenueId)
      const res = await authFetch(
        getLocalApiUrl(`/api/ecdd/bulk-import/preview?mode=${encodeURIComponent(mode)}`),
        { method: 'POST', body: fd }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      const previewRows = json.data?.rows || []
      setRows(previewRows)
      setSummary(json.data?.summary || null)
      const next = new Set()
      for (const r of previewRows) {
        if (r.status_code === 'matched') next.add(r.row_index)
      }
      setSelected(next)
    } catch (e) {
      setError(e?.message || 'Preview failed')
      setRows([])
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }

  const toggleRow = (rowIndex, on) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (on) next.add(rowIndex)
      else next.delete(rowIndex)
      return next
    })
  }

  const selectAllSelectable = () => {
    setSelected(new Set(selectableRows.map((r) => r.row_index)))
  }

  const commit = async () => {
    setCommitting(true)
    setError(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/ecdd/bulk-import/commit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode,
          rows,
          row_indexes: [...selected],
          include_name_mismatches: includeNameMismatches,
          include_already_open: includeAlreadyOpen,
          default_venue_id: defaultVenueId || undefined,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setCommitResult(json.data)
      onCommitted?.(json.data)
    } catch (e) {
      setError(e?.message || 'Commit failed')
    } finally {
      setCommitting(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} role="presentation" />
      <div className="absolute right-0 top-0 h-full w-full max-w-4xl bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <div className="font-semibold text-gray-900">Import ECDD from Excel</div>
            <p className="text-xs text-gray-500 mt-0.5">
              Download a template, fill rows, preview matches, then commit selected ECDDs.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => {
                reset()
                setMode('open_new')
              }}
              className={`text-left rounded-lg border px-4 py-3 transition-colors ${
                mode === 'open_new'
                  ? 'border-psg-teal bg-psg-teal/5 ring-1 ring-psg-teal/30'
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <div className="text-sm font-semibold text-psg-teal">Open new ECDDs</div>
              <p className="text-xs text-slate-500 m-0 mt-1">
                Member list → blank open ECDDs ready for investigation.
              </p>
            </button>
            <button
              type="button"
              onClick={() => {
                reset()
                setMode('with_data')
              }}
              className={`text-left rounded-lg border px-4 py-3 transition-colors ${
                mode === 'with_data'
                  ? 'border-psg-teal bg-psg-teal/5 ring-1 ring-psg-teal/30'
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <div className="text-sm font-semibold text-psg-teal">Import with existing data</div>
              <p className="text-xs text-slate-500 m-0 mt-1">
                Members + status, decision, and key form answers from a prior register.
              </p>
            </button>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[180px]">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Default venue (optional)</label>
              <select
                className="w-full border border-slate-200 rounded-lg text-sm px-3 py-2 bg-white"
                value={defaultVenueId}
                onChange={(e) => setDefaultVenueId(e.target.value)}
              >
                <option value="">Use venue_id column</option>
                {venues.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} ({v.id})
                  </option>
                ))}
              </select>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>
              <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
              Download {mode === 'with_data' ? 'with-data' : 'open-new'} template
            </Button>
          </div>

          <div className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">Excel file (.xlsx)</label>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="block w-full text-sm text-gray-600"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) runPreview(f)
              }}
            />
            {fileName && <p className="text-xs text-gray-500 mt-2">Loaded: {fileName}</p>}
            <p className="text-xs text-gray-500 mt-2">
              {mode === 'with_data'
                ? 'Required: member_id, venue_id. Optional: status, decision, KYC/SoF/activity/screening fields, external_ref.'
                : 'Required: member_id, venue_id. Optional: member_name, trigger_type, trigger_description, assigned_to.'}
            </p>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
          )}

          {loading && <div className="text-sm text-gray-600">Parsing and matching members…</div>}

          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              {Object.entries(summary).map(([k, v]) => (
                <div key={k} className="rounded border bg-white px-3 py-2">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">{k.replace(/_/g, ' ')}</div>
                  <div className="font-semibold text-gray-900">{v}</div>
                </div>
              ))}
            </div>
          )}

          {rows.length > 0 && !commitResult && (
            <>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeNameMismatches}
                    onChange={(e) => setIncludeNameMismatches(e.target.checked)}
                  />
                  Include name mismatches
                </label>
                {mode === 'open_new' && (
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={includeAlreadyOpen}
                      onChange={(e) => setIncludeAlreadyOpen(e.target.checked)}
                    />
                    Allow members who already have an open ECDD
                  </label>
                )}
                <button type="button" className="text-psg-teal underline text-xs" onClick={selectAllSelectable}>
                  Select all eligible ({selectableRows.length})
                </button>
              </div>

              <div className="overflow-x-auto border rounded-md">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-2 py-2 text-left">Add</th>
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">Match</th>
                      <th className="px-2 py-2 text-left">Member</th>
                      <th className="px-2 py-2 text-left">Profile</th>
                      <th className="px-2 py-2 text-left">Venue</th>
                      {mode === 'with_data' && <th className="px-2 py-2 text-left">Status</th>}
                      <th className="px-2 py-2 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rows.map((r) => {
                      const eligible = defaultSelectable(r)
                      const checked = selected.has(r.row_index)
                      return (
                        <tr key={r.row_index} className={eligible ? '' : 'bg-gray-50/60'}>
                          <td className="px-2 py-2">
                            <input
                              type="checkbox"
                              disabled={!eligible}
                              checked={checked && eligible}
                              onChange={(e) => toggleRow(r.row_index, e.target.checked)}
                            />
                          </td>
                          <td className="px-2 py-2 text-gray-500">{r.row_index}</td>
                          <td className="px-2 py-2">
                            <StatusBadge status={r.status_code} />
                          </td>
                          <td className="px-2 py-2">
                            <div className="font-mono text-xs">{r.member_id || '—'}</div>
                            <div className="text-xs text-gray-500">{r.member_name || ''}</div>
                          </td>
                          <td className="px-2 py-2 text-xs">
                            {r.profile ? (
                              <span>
                                {r.profile.display_name}
                                {r.open_ecdds?.length > 0 && (
                                  <span className="block text-orange-700 mt-0.5">
                                    Open: {r.open_ecdds.join(', ')}
                                  </span>
                                )}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs">{r.venue_id || '—'}</td>
                          {mode === 'with_data' && (
                            <td className="px-2 py-2 text-xs">
                              {r.status || 'open'}
                              {r.decision ? ` · ${r.decision}` : ''}
                            </td>
                          )}
                          <td className="px-2 py-2 text-xs text-gray-600">
                            {(r.messages || []).join('; ') || '—'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-psg-teal hover:bg-psg-teal-700"
                  disabled={committing || selected.size === 0}
                  onClick={commit}
                >
                  {committing
                    ? 'Importing…'
                    : `Import ${[...selected].filter((i) => selectableRows.some((r) => r.row_index === i)).length} ECDD${selected.size === 1 ? '' : 's'}`}
                </Button>
              </div>
            </>
          )}

          {commitResult && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 space-y-2">
              <div className="text-sm font-semibold text-green-800">Import complete</div>
              <div className="text-sm text-green-900">
                Created {commitResult.summary?.created ?? 0} · Skipped{' '}
                {commitResult.summary?.skipped ?? 0} · Errors {commitResult.summary?.errors ?? 0}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={handleClose}>
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
